import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '../context/AuthContext';
import axios from 'axios';
import { BookOpen, Clock, Wind, Sparkles, Heart, TrendingUp, Calendar, MessageSquare, ShoppingCart, Star } from 'lucide-react';

export default function Dashboard() {
  const { user, token } = useAuth();
  const [stats, setStats] = useState({ total_entries: 0, mood_counts: {} });
  const [recentProgress, setRecentProgress] = useState([]);
  const [affirmation, setAffirmation] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [moodStats, progress, dailyAffirmation] = await Promise.all([
        axios.get(`${API}/mood-entries/stats`, { headers }),
        axios.get(`${API}/progress`, { headers }),
        axios.get(`${API}/affirmations/random`).catch(() => ({ data: null }))
      ]);

      setStats(moodStats.data);
      setRecentProgress(progress.data.slice(0, 5));
      setAffirmation(dailyAffirmation.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    }
  };

  const quickActions = [
    { to: '/meditations', icon: BookOpen, label: 'Meditations', color: 'from-cosmic-purple to-cosmic-pink' },
    { to: '/timer', icon: Clock, label: 'Timer', color: 'from-cosmic-celestial to-cosmic-turquoise' },
    { to: '/breathing', icon: Wind, label: 'Breathing', color: 'from-cosmic-turquoise to-chakra-heart' },
    { to: '/journal', icon: Heart, label: 'Journal', color: 'from-cosmic-pink to-cosmic-lotus' },
    { to: '/community', icon: MessageSquare, label: 'Community', color: 'from-cosmic-gold to-cosmic-purple' },
    { to: '/shop', icon: ShoppingCart, label: 'Shop', color: 'from-cosmic-lotus to-cosmic-gold' },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="glass-card p-8 relative overflow-hidden">
        <div className="absolute inset-0 sacred-geometry opacity-20"></div>
        <div className="relative z-10">
          <div className="flex items-center space-x-2 mb-2">
            <Star className="w-6 h-6 animate-pulse-glow" style={{ color: 'var(--theme-primary)' }} />
            <h1 className="text-2xl sm:text-3xl font-cinzel gold-text">Welcome back, {user?.name}</h1>
          </div>
          <p className="font-cormorant text-lg" style={{ color: 'var(--theme-text-muted)' }}>Your cosmic journey continues today</p>
        </div>
      </div>

      {affirmation && (
        <div className="glass-card p-8 relative overflow-hidden group" data-testid="daily-affirmation">
          <div className="absolute inset-0 bg-gradient-to-r from-cosmic-gold/5 to-cosmic-purple/5"></div>
          <div className="relative z-10">
            <div className="flex items-center space-x-2 mb-4">
              <Sparkles className="w-6 h-6 animate-twinkle" style={{ color: 'var(--theme-primary)' }} />
              <h2 className="text-xl font-cinzel" style={{ color: 'var(--theme-primary)' }}>Today's Cosmic Affirmation</h2>
            </div>
            <p className="text-2xl font-cormorant italic text-center py-6 leading-relaxed" style={{ color: 'var(--theme-text)' }}>
              "{affirmation.text}"
            </p>
            <p className="text-sm text-center font-cormorant" style={{ color: 'var(--theme-text-muted)' }}>
              ~ {affirmation.category} ~
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {quickActions.map((action) => {
          const Icon = action.icon;
          return (
            <Link
              key={action.to}
              to={action.to}
              className="glass-card p-6 text-center group hover:scale-105 transition-all duration-300"
              data-testid={`quick-action-${action.label.toLowerCase()}`}
            >
              <div className={`w-14 h-14 mx-auto mb-4 bg-gradient-to-br ${action.color} rounded-xl flex items-center justify-center group-hover:shadow-lg group-hover:shadow-cosmic-gold/20 transition-all`}>
                <Icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-sm font-cinzel transition-colors" style={{ color: 'var(--theme-text)' }}>{action.label}</h3>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="glass-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-cormorant" style={{ color: 'var(--theme-text-muted)' }}>Mood Entries</p>
              <p className="text-3xl font-cinzel gold-text">{stats.total_entries}</p>
            </div>
            <div className="w-14 h-14 bg-cosmic-pink/20 rounded-xl flex items-center justify-center">
              <Heart className="w-7 h-7 text-cosmic-pink" />
            </div>
          </div>
        </div>

        <div className="glass-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-cormorant" style={{ color: 'var(--theme-text-muted)' }}>Sessions Done</p>
              <p className="text-3xl font-cinzel gold-text">{recentProgress.length}</p>
            </div>
            <div className="w-14 h-14 bg-chakra-heart/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-7 h-7 text-chakra-heart" />
            </div>
          </div>
        </div>

        <div className="glass-card p-6 group cursor-pointer" onClick={() => window.location.href = '/healing-sessions'}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-cormorant" style={{ color: 'var(--theme-text-muted)' }}>Healing Sessions</p>
              <p className="text-lg font-cinzel text-cosmic-turquoise group-hover:text-cosmic-goldLight transition-colors">Book Now</p>
            </div>
            <div className="w-14 h-14 bg-cosmic-turquoise/20 rounded-xl flex items-center justify-center">
              <Calendar className="w-7 h-7 text-cosmic-turquoise" />
            </div>
          </div>
        </div>
      </div>

      {recentProgress.length > 0 && (
        <div className="glass-card p-6">
          <h2 className="text-xl font-cinzel text-cosmic-goldLight mb-4">Recent Activity</h2>
          <div className="space-y-3">
            {recentProgress.map((activity) => (
              <div key={activity.id} className="flex items-center justify-between py-3 border-b border-cosmic-gold/10 last:border-0">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-cosmic-purple/20 rounded-lg flex items-center justify-center">
                    {activity.activity_type === 'meditation' && <BookOpen className="w-5 h-5 text-cosmic-purple" />}
                    {activity.activity_type === 'breathing' && <Wind className="w-5 h-5 text-cosmic-turquoise" />}
                    {activity.activity_type === 'timer' && <Clock className="w-5 h-5 text-cosmic-celestial" />}
                  </div>
                  <div>
                    <p className="font-cormorant text-cosmic-starWhite capitalize">{activity.activity_type}</p>
                    {activity.duration && <p className="text-sm text-cosmic-starWhite/50">{activity.duration} minutes</p>}
                  </div>
                </div>
                <p className="text-sm text-cosmic-starWhite/50 font-cormorant">
                  {new Date(activity.completed_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
